import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { api } from "../api/client";
import type { MissingVariable } from "../api/types";

export default function ConfigStatusBanner() {
  const [missing, setMissing] = useState<MissingVariable[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api
      .getConfigStatus()
      .then((res) => setMissing(res.missing))
      .catch((err: unknown) => {
        console.error("Failed to load config status", err);
      });
  }, []);

  if (dismissed || missing.length === 0) return null;

  const grouped = missing.reduce<Record<string, MissingVariable[]>>(
    (acc, v) => {
      (acc[v.category] ??= []).push(v);
      return acc;
    },
    {},
  );

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2.5 }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            {expanded ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <AlertTitle sx={{ fontWeight: 700, mb: 0, color: "inherit" }}>
        {missing.length} pipeline variable{missing.length !== 1 && "s"} not
        configured
      </AlertTitle>
      <Typography variant="body2" sx={{ color: "inherit", opacity: 0.8, mt: 0.25 }}>
        Set these as CI/CD variables in GitLab to enable full functionality.
      </Typography>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.entries(grouped).map(([category, vars]) => (
            <Box key={category}>
              <Chip
                label={category}
                size="small"
                color="warning"
                sx={{ mb: 1 }}
              />
              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                {vars.map((v) => (
                  <li key={v.env_var}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        fontFamily: "var(--flux-font-mono)",
                        fontWeight: 600,
                        color: "inherit",
                        fontSize: "0.8rem",
                      }}
                    >
                      {v.env_var}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ color: "inherit", opacity: 0.7, ml: 1 }}
                    >
                      &mdash; {v.description}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
}
